import { endOfMonth, endOfYear } from "date-fns";
import format from "date-fns/format";
import eachMonthOfInterval from "date-fns/fp/eachMonthOfInterval";
import eachYearOfInterval from "date-fns/fp/eachYearOfInterval";
import getMonth from "date-fns/fp/getMonth";
import getYear from "date-fns/fp/getYear";
import en from "date-fns/locale/en-US";
import ro from "date-fns/locale/ro";
import { isEmpty } from "lodash";
import compose from "lodash/fp/compose";
import map from "lodash/fp/map";
import orderBy from "lodash/fp/orderBy";
import createCachedSelector from "re-reselect";
import { getI18n } from "react-i18next";
import { createSelector } from "reselect";

import i18n from "@app/i18n";

// import { CONST_LISTORIGIN_WORK } from '@app/state/insider/constants';
// import { TCommonInvoice } from '@app/types';
import { schema } from "../schema";
import { TUIInvoiceOverall } from "../schema/uiInvoiceMain.def";

const oldYearsBound = 2;
const dateLocales = { en: en, ro: ro };
const t = i18n.t.bind(i18n);
const getLanguage = () => getI18n()?.language.toUpperCase() || "EN";
const titleCase = (str: string) => {
  return str
    .toLowerCase()
    .split(" ")
    .map(function (word) {
      return word.replace(word[0], word[0].toUpperCase());
    })
    .join(" ");
};
export const fxInvoiceTrackTreeData = createSelector(
  schema.app.select,
  schema.profileGroup.select,
  schema.whoami.select,
  schema.listModels.selectTableAsList,
  getLanguage,
  (appdata, currentGroup, login, models, lng) => {
    /* this could work in jgap but not in karma
    const lgctx = login.LOGINCTX;
    const crtGid = Number(lgctx.split('/')?.[0]) || -1;
    if (crtGid === -1) {
      return [];
    }
    */

    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
    const fallBackDate = currentGroup?.GROUP_CREATED || oneYearAgo;

    const fromDate = appdata.absolutestart
      ? new Date(appdata.absolutestart)
      : new Date(fallBackDate);

    const toDate = new Date();
    const months = eachMonthOfInterval({ start: fromDate, end: toDate });
    const years = eachYearOfInterval({ start: fromDate, end: toDate });

    // first node
    const all = {
      id: "0",
      title: getI18n()?.t("tkey_allHistory"),
      parent: null,
      order: 0,
    };
    // the years
    const yearDs = compose(
      map((x: Date) => ({
        id: `${getYear(x)}`,
        title: format(new Date(x), "yyyy", {
          locale: dateLocales[lng.toLowerCase() as "en" | "ro"],
        }),
        parent: null,
        order: +getYear(x) * 10,
      }))
    )(years as Array<Date>);

    // the months, for each year, localized
    const monthDs = compose(
      map((x: Date) => ({
        id: `${getYear(x)}-${getMonth(x)}`,
        title: titleCase(
          format(new Date(x), "MMM", {
            locale: dateLocales[lng.toLowerCase() as "en" | "ro"],
          })
        ),
        parent: getYear(x),
        order: +getYear(x) * 10 + getMonth(x),
      }))
    )(months as Array<Date>);
    let collapseOlder = {};

    if (years.length > oldYearsBound) {
      //create special node for collapsing older years
      collapseOlder = {
        id: "collapse-older",
        title: `${years[0].getFullYear()} ... ${years[
          years.length - oldYearsBound - 1
        ].getFullYear()}`,
        parent: null,
        order: +getYear(years.at(-oldYearsBound)) * 10 - 1,
      };
    }

    return orderBy(
      ["order"],
      ["asc"],
      [
        all,
        ...yearDs,
        ...(isEmpty(collapseOlder) ? [] : [collapseOlder]),
        ...monthDs,
      ]
    );
  }
);
export const fxInvoiceTrackTreeDataFolded = createSelector(
  schema.app.select,
  schema.profileGroup.select,
  schema.whoami.select,
  schema.listModels.selectTableAsList,
  getLanguage,
  (appdata, currentGroup, login, models, lng) => {
    /* this could work in jgap but not in karma
    const lgctx = login.LOGINCTX;
    const crtGid = Number(lgctx.split('/')?.[0]) || -1;
    if (crtGid === -1) {
      return [];
    }
    */

    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
    const fallBackDate = currentGroup?.GROUP_CREATED || oneYearAgo;

    const fromDate = appdata.absolutestart
      ? new Date(appdata.absolutestart)
      : new Date(fallBackDate);

    const toDate = new Date();
    // const months_ = eachMonthOfInterval({ start: fromDate, end: toDate });
    const years_ = eachYearOfInterval({ start: fromDate, end: toDate });
    let years = years_;
    let older = {};
    if (years_.length > oldYearsBound) {
      //create special node for older years
      older = {
        id: "older",
        title: `${years_[0].getFullYear()} ... ${years_[
          years_.length - oldYearsBound - 1
        ].getFullYear()}`,
        parent: null,
        order: 1,
      };
      years = years_.slice(-oldYearsBound);
    }

    const months = eachMonthOfInterval({ start: years[0], end: toDate });
    // first node
    const all = {
      id: "0",
      title: getI18n()?.t("tkey_allHistory"),
      parent: null,
      order: 0,
    };
    // the years
    const yearDs = compose(
      map((x: Date) => ({
        id: `${getYear(x)}`,
        title: format(new Date(x), "yyyy", {
          locale: dateLocales[lng.toLowerCase() as "en" | "ro"],
        }),
        parent: null,
        order: +getYear(x) * 10,
      }))
    )(years as Array<Date>);
    // the months, for each year, localized
    const monthDs = compose(
      map((x: Date) => ({
        id: `${getYear(x)}-${getMonth(x)}`,
        title: titleCase(
          format(new Date(x), "MMM", {
            locale: dateLocales[lng.toLowerCase() as "en" | "ro"],
          })
        ),
        parent: getYear(x),
        order: +getYear(x) * 10 + getMonth(x),
      }))
    )(months as Array<Date>);

    return orderBy(
      ["order"],
      ["asc"],
      [all, ...(isEmpty(older) ? [] : [older]), ...yearDs, ...monthDs]
    );
  }
);

export function filterRange(filterProps: Partial<TUIInvoiceOverall>) {
  const { currentItem, includePrevious } = filterProps;
  let fromDate = new Date(1970, 1, 1);
  let toDate = new Date();
  if (currentItem && currentItem !== "0") {
    const [year, month] = currentItem.split("-");
    //Year selection
    if (month === undefined || month === null) {
      //will select the whole year
      fromDate = new Date(Number(year), 0, 1);
      //end of year on today date
      toDate = endOfYear(fromDate);
    }
    //Month selection
    else {
      //start of month on currentItem
      fromDate = new Date(Number(year), Number(month), 1);
      //end of month on currentItem
      toDate = endOfMonth(fromDate);
    }
    //All history
  } else {
    // end of month on today date
    toDate = endOfMonth(new Date());
  }
  if (includePrevious) {
    fromDate = new Date(1970, 1, 1);
    //the end is already set
  }
  return { fromDate, toDate };
}

export const selectFxFilterRange = createCachedSelector(
  schema.uiInvoiceOverall.select,
  (filterProps): { fromDate: Date; toDate: Date } => {
    return filterRange(filterProps);
  }
)((state, _) => {
  return `${state["uiInvoiceOverall"].currentItem}:${state["uiInvoiceOverall"].includePrevious}`;
});

export const isCurrentMonthSelected = createSelector(
  schema.uiInvoiceOverall.select,
  (current) => {
    const todayIncluded = [
      "0",
      `${getYear(new Date())}`,
      `${getYear(new Date())}-${getMonth(new Date())}`,
    ];
    return todayIncluded.includes(current.currentItem);
  }
);
